// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { viewStyles } from "@flip/Overrides/Widgets/ProductDetails/ProductDetailsView";
import { DivWrapper } from "@flip/XDL/constants";
import { avoidBreakInsidePrintElement, color, hideElementOnPrint, xScreen } from "@flip/XDL/Utilities";
import { css } from "styled-components";

viewStyles.container = {
    css: css`
        display: flex;
        margin-bottom: 64px;
        gap: 22px;
        width: 100%;

        ${xScreen("768", "1024")} {
            margin-bottom: 24px;
        }

        ${xScreen("lte", "md")} {
            flex-wrap: wrap;
            justify-content: space-between;
        }

        ${xScreen("lte", "xs")} {
            flex-wrap: wrap;
            justify-content: space-between;
        }

        @media print {
            display: flex !important;
            flex-wrap: wrap;
        }
    `,
};

viewStyles.imagesSection = {
    css: css`
        flex: 0 0 328px;
        max-width: 328px;
        order: 1;
        ${xScreen("lte", "md")} {
            order: 2;
            flex: 0 1 350px;
        }
        ${xScreen("lte", "xs")} {
            order: 2;
            flex: 1 0 100%;
            max-width: 100%;
        }
        @media print {
            order: 2;
            flex: 0 1 300px;
        }
        img {
            object-fit: contain;
        }
    `,
};

/* XNG-Changes: FLIP-819 */
viewStyles.mainSection = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        order: 2;
        ${xScreen("lte", "md")} {
            order: 3;
            flex: 1 0 350px;
            margin-top: 32px;
        }
        ${xScreen("lte", "xs")} {
            order: 3;
            flex: 1 0 100%;
            margin-top: 0;
        }
        @media print {
            order: 4;
            flex: 1 0 100%;
        }
    `,
};

/* XNG-Changes: FLIP-819  */
viewStyles.rightSection = {
    css: css`
        flex: 0 0 316px;
        order: 3;
        min-width: 316px;
        ${xScreen("lte", "md")} {
            order: 4;
        }
        ${xScreen("lte", "xs")} {
            min-width: auto;
            order: 4;
            flex: 1 0 100%;
        }
        @media print {
            order: 4;
            padding: 15px 12px;
        }
    `,
};

viewStyles.actionsWrapper = {
    css: css`
        border: 1px solid ${color("common.border")};
        border-radius: 8px;
        margin-bottom: 8px;
        padding: 16px;
        ${avoidBreakInsidePrintElement}
        @media print {
            div[data-widget="ProductDetails/ProductDetailsActionsTopSection"] {
                display: none;
            }
        }
        legend[data-test-selector="multiple-sale-quantity"] {
            margin-top: 0;
        }
        ${({ productHasMultipleSaleQty }: any) => {
            if (productHasMultipleSaleQty) {
                return css`
                    legend[data-test-selector="minimum-ordered-quantity"] {
                        margin-bottom: 4px;
                    }
                `;
            }
        }}
    `,
};

viewStyles.stickyWrapper = {
    css: css`
        position: fixed;
        left: 0;
        background: ${color("common.white")};
        width: 100%;
        z-index: 1300;
        transition: all 300ms ease-out;
        opacity: 1;
        pointer-events: initial;
        height: 92px;
        height: auto;
        box-shadow: 0 1px 0 0 ${color("common.border")};

        &.stickyVisible {
            top: 0;
        }

        &.stickyHidden {
            top: -250px;
            &,
            & > * {
                max-height: 0;
                overflow: hidden;
            }
        }

        &.editingMode {
            position: unset;
        }

        ${xScreen("lte", "md")} {
            display: none;
        }

        ${hideElementOnPrint}
    `,
};

viewStyles.headerPage = {
    css: css`
        &&&& {
            padding-top: 12px;
            padding-bottom: 12px;
        }
    `,
};

viewStyles.productTitleHiddenMobile = {
    css: css`
        flex-basis: 100%;
        order: 1;
        @media print {
            order: 1;
        }
    `,
};

viewStyles.partNumbersSection = {
    css: css`
        ${DivWrapper} {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
    `,
};

viewStyles.productPriceAndActionsStyle = {
    productGridContainer: {
        price: {
            css: css`
                [data-test-selector="productPrice_unitNetPrice"] {
                    font-size: 20px;
                    line-height: 25px;
                }
            `,
        },
        priceAndActions: {
            css: css`
                background-color: transparent;
                padding: 0;
            `,
        },
    },
};

viewStyles.bottomSection = {
    css: css`
        margin-bottom: 64px;
    `,
};

viewStyles.tariffTextStyle = {
    variant: "legend",
    color: "common.ui12",
    weight: "bold",
    italic: true,
    css: css`
        display: block;
        margin-top: 8px;
    `,
};
